import { CORPORATION_REQUIRED_FIELDS } from "src/core/constants/corporation-fields";
import { EI_REQUIRED_FIELDS } from "src/core/constants/ei-fields";
import { EReputationStatus } from "src/core/constants/eReputation";
import { FolderFileDocumentCode, MappedStakeholder } from "src/core/constants/folder";
import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel, getRiskLevelValue } from "src/core/constants/risk";
import { STAKEHOLDER_REQUIRED_FIELDS } from "src/core/constants/stakeholder-fields";
import { DocumentStatus, ElementInputValue, FolderFileQuery, Mission } from "src/core/gql/graphql";
import { IQuestionnaire } from "src/core/interfaces/Questionnaire";
import { getFolderFieldValue, mapMissionToIMission } from "src/core/mappers/folder.mapper";
import { MappedEntity } from "src/core/types/entity";

export const getInputValue = (
  inputValues: ElementInputValue[],
  fieldCode: string
): string | null => {
  const inputValue = inputValues.find((iv) => iv.inputField.fieldCode === fieldCode);
  return inputValue?.stringValue || null;
};

const countMissingFields = (
  requiredFields: readonly string[],
  inputValues: ElementInputValue[]
): number => requiredFields.filter((field) => !getInputValue(inputValues, field)).length;

/**
 * Calcul le nombre d'informations manquantes pour l'entreprise.
 */
export const calculateCompanyMissingInfo = (folderFile: FolderFileQuery): number => {
  let count = 0;

  count += calculateCompanyMissingDocuments(folderFile);
  count += calculateCompanyMissingFields(folderFile);

  return count;
};

/**
 * Calcul le nombre d'informations manquantes pour les stakeholders (parties prenantes).
 */
export const calculateStakeholdersMissingInfo = (
  folderFile: FolderFileQuery,
  isPPH = false
): {
  total: number;
  details: Record<string, number>;
} => {
  const stakeholders = folderFile.folderFile?.fileCustomerFolder?.stakeholders;
  if (!stakeholders || stakeholders.length === 0) return { total: 0, details: {} };

  let total = 0;
  const details: Record<string, number> = {};

  if (isLegalRepresentativeMissing(stakeholders) && !isPPH) total++;

  // Vérification des informations manquantes pour chaque stakeholder qui n'est pas l'entreprise.
  stakeholders
    .filter((stakeholder) => isPPH || !stakeholder.isFolderCustomer)
    .forEach((stakeholder) => {
      let detailCount = 0;
      const inputValues = stakeholder.inputValues as ElementInputValue[];
      detailCount += countMissingFields(
        isPPH ? EI_REQUIRED_FIELDS : STAKEHOLDER_REQUIRED_FIELDS,
        inputValues
      );
      detailCount += stakeholder.supportingDocuments.filter(
        (document) =>
          document.mainDocument === null &&
          document.abstractDocumentType?.code !== FolderFileDocumentCode.MISSION_LETTER
      ).length;
      details[stakeholder.entity.id] = detailCount;
      total += detailCount;
    });

  return { total, details };
};

/**
 * Vérifie si un représentant légal est présent parmi les stakeholders.
 */
const isLegalRepresentativeMissing = (
  stakeholders: NonNullable<
    NonNullable<FolderFileQuery["folderFile"]>["fileCustomerFolder"]["stakeholders"]
  >
): boolean => {
  const hasLegalRepresentative = stakeholders.some(
    (stakeholder) => stakeholder.isLegalRepresentative
  );

  return !hasLegalRepresentative;
};

/**
 * Calcul le nombre d'informations manquantes pour les stakeholders (parties prenantes).
 * Pour la brand "EC".
 */
export const calculateAllStakeholdersMissingInfo = (
  folderFile: FolderFileQuery,
  isPPH = false
): {
  totalStakeholdersWithInfoMissing: number;
  totalDocumentsMissing: number;
  details: Record<string, number>;
  isLegalRepresentativeMissing: boolean;
} => {
  const stakeholders = folderFile.folderFile?.fileCustomerFolder?.stakeholders;
  if (!stakeholders || stakeholders.length === 0)
    return {
      totalStakeholdersWithInfoMissing: 0,
      totalDocumentsMissing: 0,
      details: {},
      isLegalRepresentativeMissing: false,
    };

  let totalStakeholdersWithInfoMissing = 0;
  let totalDocumentsMissing = 0;
  const details: Record<string, number> = {};

  // Vérification des informations manquantes pour chaque stakeholder.
  stakeholders.forEach((stakeholder) => {
    let documentsCount = 0;
    const inputValues = stakeholder.inputValues as ElementInputValue[];
    const infoMissingCount = countMissingFields(
      isPPH
        ? EI_REQUIRED_FIELDS
        : stakeholder.isFolderCustomer
        ? CORPORATION_REQUIRED_FIELDS
        : STAKEHOLDER_REQUIRED_FIELDS,
      inputValues
    );

    documentsCount = stakeholder.supportingDocuments.filter(
      (document) =>
        (document.mainDocument === null ||
          (document.mainDocument?.__typename === "Document" &&
            document.mainDocument.status === DocumentStatus.Expired)) &&
        document.abstractDocumentType?.code !== FolderFileDocumentCode.MISSION_LETTER
    ).length;
    details[stakeholder.entity.id] = infoMissingCount + documentsCount;
    totalStakeholdersWithInfoMissing += infoMissingCount > 0 ? 1 : 0;
    totalDocumentsMissing += documentsCount;
  });

  return {
    totalStakeholdersWithInfoMissing,
    totalDocumentsMissing,
    details,
    isLegalRepresentativeMissing: isLegalRepresentativeMissing(stakeholders),
  };
};

export const calculateCompanyMissingDocuments = (folderFile: FolderFileQuery): number => {
  const company = folderFile.folderFile?.fileCustomerFolder?.stakeholders?.find(
    (stakeholder) => stakeholder.isFolderCustomer
  );
  if (!company) return 0;
  return company.supportingDocuments.filter(
    (document) =>
      document.mainDocument === null &&
      document.abstractDocumentType?.code !== FolderFileDocumentCode.MISSION_LETTER
  ).length;
};

/**
 * Calcul le nombre de champs manquants pour l'onglet Entreprise.
 */
export const calculateCompanyMissingFields = (folderFile: FolderFileQuery): number => {
  const customer = folderFile.folderFile?.fileCustomerFolder?.customer;

  let count = 0;

  const inputValues = customer?.allInputValues as ElementInputValue[];
  count += countMissingFields(CORPORATION_REQUIRED_FIELDS, inputValues);

  // Cas particulier : si la forme juridique est "other", alors il faut vérifier le champ "customString3"
  // qui correspond à "Préciser la forme juridique"
  const legalForm = getInputValue(inputValues, "customListCode2");
  if (legalForm === "other") {
    count += countMissingFields(["customString3"], inputValues);
  }
  return count;
};

/**
 * Calcul le nombre de champs manquants pour une entreprise individuelle.
 */
export const calculateEIMissingFields = (folderFile: FolderFileQuery): number => {
  const customer = folderFile.folderFile?.fileCustomerFolder?.customer;

  let count = 0;

  const inputValues = customer?.allInputValues as ElementInputValue[];
  count += countMissingFields(EI_REQUIRED_FIELDS, inputValues);

  return count;
};

/**
 * Calcul le niveau de risque de l'entreprise.
 */
export const calculateCorporationRiskLevel = (
  folderFile: FolderFileQuery,
  countryWithRiskOptions: IRiskOption[],
  activityWithRiskOptions: IRiskOption[],
  legalTypesWithRiskOptions: IRiskOption[],
  isPPH = false
): CustomRiskLevel => {
  const regsitrationCountryCode = getFolderFieldValue(folderFile, "registrationCountryCode");
  const activity = getFolderFieldValue(folderFile, "activityCode");
  const legalType = getFolderFieldValue(folderFile, "entityTypeCode", "unlisted");

  let riskLevel: CustomRiskLevel = CustomRiskLevel.UNDEFINED;

  [regsitrationCountryCode, activity, legalType].forEach((value) => {
    if (value) {
      const countryRiskLevel = countryWithRiskOptions.find((country) => country.value === value)
        ?.risk;
      if (countryRiskLevel && getRiskLevelValue(countryRiskLevel) > getRiskLevelValue(riskLevel)) {
        riskLevel = countryRiskLevel;
      }

      const activityRiskLevel = activityWithRiskOptions.find((activity) => activity.value === value)
        ?.risk;
      if (
        activityRiskLevel &&
        getRiskLevelValue(activityRiskLevel) > getRiskLevelValue(riskLevel)
      ) {
        riskLevel = activityRiskLevel;
      }
      if (!isPPH) {
        const legalTypeRiskLevel = legalTypesWithRiskOptions.find(
          (legalType) => legalType.value === value
        )?.risk;
        if (
          legalTypeRiskLevel &&
          getRiskLevelValue(legalTypeRiskLevel) > getRiskLevelValue(riskLevel)
        ) {
          riskLevel = legalTypeRiskLevel;
        }
      }
    }
  });

  return riskLevel;
};

/**
 * Calcul le niveau de risque des stakeholders du dossier (parties prenantes).
 */
export const calculateStakeholdersRiskLevel = (
  mappedStakeholders: MappedStakeholder[],
  countryWithRiskOptions: IRiskOption[]
): CustomRiskLevel => {
  const stakeholders = mappedStakeholders.filter((s) => !s.isFolderCustomer);

  let riskLevel: CustomRiskLevel = CustomRiskLevel.UNDEFINED;

  stakeholders.forEach((stakeholder) => {
    const countryRiskLevel = countryWithRiskOptions.find(
      (country) => country.value === stakeholder.residenceCountryCode
    )?.risk;

    if (countryRiskLevel && getRiskLevelValue(countryRiskLevel) > getRiskLevelValue(riskLevel)) {
      riskLevel = countryRiskLevel;
    }
  });

  return riskLevel;
};

export const calculateQuestionnaireRiskLevel = (
  questionnaireLAB: IQuestionnaire
): CustomRiskLevel => {
  if (!questionnaireLAB?.questions || questionnaireLAB.questions.length === 0)
    return CustomRiskLevel.UNDEFINED;

  const hasTrueAnswer = questionnaireLAB.questions.some((question) => question.answer === true);
  const hasUndefinedAnswer = questionnaireLAB.questions.some(
    (question) => question.answer === undefined
  );
  const allFalse = questionnaireLAB.questions.every((question) => question.answer === false);

  if (hasTrueAnswer) return CustomRiskLevel.HIGH;
  if (allFalse) return CustomRiskLevel.LOW;
  if (hasUndefinedAnswer) return CustomRiskLevel.UNDEFINED;

  return CustomRiskLevel.UNDEFINED;
};

export const calculateQuestionnaireMissingQuestions = (questionnaireLAB: IQuestionnaire) => {
  if (!questionnaireLAB?.questions) return 1;

  return questionnaireLAB.questions.some((question) => question.answer === undefined) ? 1 : 0;
};

/**
 * Calcul le niveau de risque pour l'onglet "Alerte".
 */
export const calculateAlerteRiskLevel = (entities: MappedEntity[]): CustomRiskLevel => {
  if (!entities || entities.length === 0) return CustomRiskLevel.UNDEFINED;

  let highestRiskLevel: CustomRiskLevel = CustomRiskLevel.UNDEFINED;

  entities.forEach((entity) => {
    // Vérifier le statut PEP
    if (entity.pepStatus === EReputationStatus.CONFIRMED) {
      highestRiskLevel = CustomRiskLevel.HIGH;
    } else if (
      [
        EReputationStatus.FALSE_POSITIVE,
        EReputationStatus.NO_ANALYSIS,
        EReputationStatus.NO_ALERT,
      ].includes(entity.pepStatus) &&
      getRiskLevelValue(highestRiskLevel) < getRiskLevelValue(CustomRiskLevel.LOW)
    ) {
      highestRiskLevel = CustomRiskLevel.LOW;
    }

    // Vérifier le statut de sanction
    if (entity.sanctionStatus === EReputationStatus.CONFIRMED) {
      highestRiskLevel = CustomRiskLevel.HIGH;
    } else if (
      [
        EReputationStatus.FALSE_POSITIVE,
        EReputationStatus.NO_ANALYSIS,
        EReputationStatus.NO_ALERT,
      ].includes(entity.sanctionStatus) &&
      getRiskLevelValue(highestRiskLevel) < getRiskLevelValue(CustomRiskLevel.LOW)
    ) {
      highestRiskLevel = CustomRiskLevel.LOW;
    }
  });

  return highestRiskLevel;
};

/**
 * Calcul le nombre d'informations manquantes pour l'onglet "Alerte".
 */
export const calculateAlerteMissingInfo = (entities: MappedEntity[]): number => {
  let count = 0;

  entities.forEach((entity) => {
    const hasMissingInfo =
      entity.pepStatus === EReputationStatus.POTENTIAL ||
      entity.sanctionStatus === EReputationStatus.POTENTIAL ||
      entity.pepStatus === EReputationStatus.NO_ANALYSIS ||
      entity.sanctionStatus === EReputationStatus.NO_ANALYSIS;

    if (hasMissingInfo) {
      count++;
    }
  });

  return count;
};
/**
 * Calcul le nombre d'informations manquantes pour l'onglet "Alerte".
 */
export const calculateAlerteMissingInfoEC = (entities: MappedEntity[]): number => {
  let count = 0;

  entities.forEach((entity) => {
    const hasMissingInfo =
      entity.pepStatus === EReputationStatus.POTENTIAL ||
      entity.sanctionStatus === EReputationStatus.POTENTIAL;

    if (hasMissingInfo) {
      count++;
    }
  });

  return count;
};

/**
 * Calcul le nombre d'entités non analysées.
 */
export const calculateAlerteMissingInfoNoAnalysis = (entities: MappedEntity[]): number => {
  let count = 0;

  for (const entity of entities) {
    const hasMissingInfo =
      entity.pepStatus === EReputationStatus.NO_ANALYSIS ||
      entity.sanctionStatus === EReputationStatus.NO_ANALYSIS;
    if (hasMissingInfo) {
      count++;
      break;
    }
  }

  return count;
};

/**
 * Calcul le nombre de missions manquantes pour l'onglet "Missions".
 */
export const calculateFolderMissingMissions = (
  folderFile: FolderFileQuery,
  missionsWithRiskOptions: IRiskOption[]
): number => {
  const missions = folderFile.folderFile?.fileCustomerFolder?.missions?.map((mission) =>
    mapMissionToIMission(mission as Mission, missionsWithRiskOptions)
  );
  if (!missions || missions.length === 0) return 1;

  let activeMissions = false;
  missions.forEach((mission) => {
    if (mission.isActive) {
      activeMissions = true;
    }
  });

  return activeMissions ? 0 : 1;
};

export const calculateFolderMissionsRiskLevel = (
  folderFile: FolderFileQuery,
  missionsWithRiskOptions: IRiskOption[]
): CustomRiskLevel => {
  const missions = folderFile.folderFile?.fileCustomerFolder?.missions?.map((mission) =>
    mapMissionToIMission(mission as Mission, missionsWithRiskOptions)
  );
  if (!missions || missions.length === 0) return CustomRiskLevel.UNDEFINED;

  const activeMissions = missions.filter((mission) => mission.isActive);
  if (!activeMissions || activeMissions.length === 0) return CustomRiskLevel.UNDEFINED;

  return activeMissions.reduce<CustomRiskLevel>(
    (acc, { risk }) => (getRiskLevelValue(risk) > getRiskLevelValue(acc) ? risk : acc),
    CustomRiskLevel.LOW
  );
};
